// src/hooks/useJobPolling.ts - FIXED: Proper job status handling
import { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../constants';
import type { Job } from '../types';

interface UseJobPollingProps {
  apiKey: string;
  pollingInterval?: number;
  onJobsUpdate: (jobs: Job[]) => void;
  onJobComplete?: (job: Job, dismiss: () => void) => void;
}

export const useJobPolling = ({
  apiKey,
  pollingInterval = 3000,
  onJobsUpdate,
  onJobComplete,
}: UseJobPollingProps) => {
  const [activeJobs, setActiveJobs] = useState<Map<string, Job>>(new Map());
  const [dismissedJobs, setDismissedJobs] = useState<Set<string>>(new Set());
  const [jobDisplayTimers, setJobDisplayTimers] = useState<Map<string, NodeJS.Timeout>>(new Map());
  const [completedJobsNotified, setCompletedJobsNotified] = useState<Set<string>>(new Set());
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const initializedRef = useRef(false);
  const activeJobsRef = useRef(activeJobs);
  activeJobsRef.current = activeJobs;

  const dismissJob = useCallback((jobId: string) => {
    
    // Clear any existing timer for this job
    setJobDisplayTimers(prev => {
      const timer = prev.get(jobId);
      if (timer) {
        clearTimeout(timer);
        const newTimers = new Map(prev);
        newTimers.delete(jobId);
        return newTimers;
      }
      return prev;
    });
    
    setDismissedJobs(prev => new Set(prev).add(jobId));
    setActiveJobs(prev => {
      const newJobs = new Map(prev);
      newJobs.delete(jobId);
      return newJobs;
    });
    
    // Clean up completion notification tracking
    setCompletedJobsNotified(prev => {
      const newSet = new Set(prev);
      newSet.delete(jobId);
      return newSet;
    });
  }, []);

  // Helper function to handle job completion (prevents duplicate notifications)
  const handleJobCompletion = useCallback((jobData: Job, previousJob: Job) => {
    if (previousJob && ['pending', 'running'].includes(previousJob.status) && 
        ['completed', 'failed', 'canceled'].includes(jobData.status)) {
      
      // Check if we've already notified about this job completion
      if (!completedJobsNotified.has(jobData.job_id)) {

        setCompletedJobsNotified(prev => new Set(prev).add(jobData.job_id));
        
        if (onJobComplete) {
          onJobComplete(jobData, () => dismissJob(jobData.job_id));
        }
        return true;
      } else {

      }
    }
    return false;
  }, [completedJobsNotified, onJobComplete, dismissJob]);

  const initializeActiveJobs = useCallback(async () => {
    if (!apiKey || initializedRef.current) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/jobs?page=1&size=50`, {
        headers: { 'Authorization': `Bearer ${apiKey}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        const jobs = data.jobs || [];
        
        const currentDismissed = dismissedJobs;
        
        const activeJobsList = jobs.filter((job: Job) => 
          (job.status === 'pending' || job.status === 'running') && !currentDismissed.has(job.job_id)
        );
        
        const recentlyCompletedJobs = jobs.filter((job: Job) => {
          if (currentDismissed.has(job.job_id)) return false;
          if (!['completed', 'failed', 'canceled'].includes(job.status)) return false;
          if (!job.completed_at) return false;
          
          const completedTime = new Date(job.completed_at).getTime();
          const now = Date.now();
          const timeSinceCompletion = now - completedTime;
          
          return timeSinceCompletion < 15000;
        });
        
        const allJobsToShow = [...activeJobsList, ...recentlyCompletedJobs];
        
        if (allJobsToShow.length > 0) {
          const jobsMap = new Map(allJobsToShow.map((job: Job) => [job.job_id, job]));
          setActiveJobs(jobsMap);
          
          // FIXED: Only set timers for completed jobs
          recentlyCompletedJobs.forEach((job: Job) => {
            const dismissTime = job.status === 'canceled' ? 2000 : 5000;
            const timer = setTimeout(() => {
              dismissJob(job.job_id);
            }, dismissTime);
            
            setJobDisplayTimers(prev => new Map(prev).set(job.job_id, timer));
          });
        }
        initializedRef.current = true;
      } else if (response.status === 401) {

        localStorage.setItem('show_login_page', 'true');
        window.location.reload();
        return;
      }
    } catch {
      // Failed to initialize active jobs
      initializedRef.current = true;
    }
  }, [apiKey, dismissJob, dismissedJobs]);

  useEffect(() => {
    initializeActiveJobs();
  }, [initializeActiveJobs]);

  const pollJobs = useCallback(async (jobsToPoll: Map<string, Job>) => {
    if (jobsToPoll.size === 0) return;

    const updatedJobs = new Map(jobsToPoll);
    let hasChanged = false;

    for (const jobId of jobsToPoll.keys()) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/jobs/${jobId}`, {
          headers: { 'Authorization': `Bearer ${apiKey}` },
        });

        if (!response.ok) {
          if (response.status === 401) {

            localStorage.setItem('show_login_page', 'true');
            window.location.reload();
            return;
          }
          continue;
        }
        
        const jobData: Job = await response.json();
        const previousJob = updatedJobs.get(jobId);
        
        // Reduced logging - only log status changes and completions
        if (previousJob && previousJob.status !== jobData.status) {

        }
        
        // Check if job status changed or result updated
        if (!previousJob || 
            previousJob.status !== jobData.status || 
            previousJob.progress !== jobData.progress ||
            JSON.stringify(previousJob.result) !== JSON.stringify(jobData.result)) {
          updatedJobs.set(jobId, jobData);
          hasChanged = true;
          
          // If job just completed, trigger completion handler (only once)
          if (previousJob) {
            handleJobCompletion(jobData, previousJob);
          }
        }
      } catch {
        // Error polling job - continue with other jobs
      }
    }

    if (hasChanged) {
      setActiveJobs(updatedJobs);
    }
  }, [apiKey, onJobComplete, dismissJob]);

  useEffect(() => {
    onJobsUpdate(Array.from(activeJobs.values()));
  }, [activeJobs, onJobsUpdate]);

  useEffect(() => {
    const jobsToPoll = new Map(
      Array.from(activeJobs.entries()).filter(([, job]) => 
        job.status === 'pending' || job.status === 'running'
      )
    );

    if (pollingRef.current) clearInterval(pollingRef.current);
    if (jobsToPoll.size > 0) {
      // Initial poll immediately
      pollJobs(jobsToPoll);
      // Then set up interval
      pollingRef.current = setInterval(() => pollJobs(jobsToPoll), pollingInterval);
    }

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [activeJobs, pollJobs, pollingInterval]);

  const addJobId = useCallback((jobId: string, jobType: Job['job_type'] = 'export') => {
    // Adding job to tracking
    
    const newJob: Job = {
      job_id: jobId,
      owner_id: '',
      job_type: jobType,
      status: 'pending',
      progress: 0,
      params: {},
      result: null,
      error: null,
      created_at: new Date().toISOString(),
      started_at: null,
      completed_at: null
    };
    
    // Clear any existing timer for this job ID
    setJobDisplayTimers(prev => {
      const timer = prev.get(jobId);
      if (timer) {
        clearTimeout(timer);
        const newTimers = new Map(prev);
        newTimers.delete(jobId);
        return newTimers;
      }
      return prev;
    });
    
    // Remove from dismissed set
    setDismissedJobs(prev => {
      const newSet = new Set(prev);
      newSet.delete(jobId);
      return newSet;
    });
    
    // Remove from completion notification tracking (in case of job retry)
    setCompletedJobsNotified(prev => {
      const newSet = new Set(prev);
      newSet.delete(jobId);
      return newSet;
    });

    setActiveJobs(prev => new Map(prev).set(jobId, newJob));
    
    // Start aggressive polling for this new job
    let quickPollCount = 0;
    const quickPoll = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/jobs/${jobId}`, {
          headers: { 'Authorization': `Bearer ${apiKey}` },
        });
        if (response.ok) {
          const jobData: Job = await response.json();
          setActiveJobs(prev => {
            const newJobs = new Map(prev);
            const previousJob = newJobs.get(jobId);
            newJobs.set(jobId, jobData);
            
            // Check if job completed (only notify once)
            if (previousJob) {
              handleJobCompletion(jobData, previousJob);
            }
            
            return newJobs;
          });
        }
      } catch {
        // Error quick-polling job
      }
      
      // Continue quick polling for active jobs
      quickPollCount++;
      if (quickPollCount < 10) { // Poll up to 10 times quickly
        const currentJob = activeJobsRef.current.get(jobId);
        if (currentJob && ['pending', 'running'].includes(currentJob.status)) {
          setTimeout(quickPoll, 500);
        }
      }
    };
    
    // Start quick polling immediately
    setTimeout(quickPoll, 100);
  }, [apiKey, onJobComplete, dismissJob]);
  
  const cancelJob = useCallback(async (jobId: string) => {
    try {
      // Attempting to cancel job
      
      // Check current job status first
      const statusResponse = await fetch(`${API_BASE_URL}/api/jobs/${jobId}`, {
        headers: { 'Authorization': `Bearer ${apiKey}` },
      });
      
      if (statusResponse.ok) {
        const currentJob: Job = await statusResponse.json();
        
        // If job is already finished, don't try to cancel
        if (['completed', 'failed', 'canceled'].includes(currentJob.status)) {
          // Job is already completed/failed, cannot cancel
          
          // Update our local state immediately
          setActiveJobs(prev => {
            const newJobs = new Map(prev);
            newJobs.set(jobId, currentJob);
            return newJobs;
          });
          
          // Trigger completion handler
          if (onJobComplete) {
            onJobComplete(currentJob, () => dismissJob(jobId));
          }
          
          return;
        }
      }
      
      // Optimistic update - immediately show as canceled in UI
      setActiveJobs(prev => {
        const newJobs = new Map(prev);
        const job = newJobs.get(jobId);
        if (job && ['pending', 'running'].includes(job.status)) {
          newJobs.set(jobId, { ...job, status: 'canceled' });
          // Optimistically set job to canceled
        }
        return newJobs;
      });
      
      // Make the actual cancellation request
      const cancelResponse = await fetch(`${API_BASE_URL}/api/jobs/${jobId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${apiKey}` },
      });
      
      if (!cancelResponse.ok) {
        // Job cancellation failed
        
        // Rollback optimistic update if cancellation failed
        setActiveJobs(prev => {
          const newJobs = new Map(prev);
          const job = newJobs.get(jobId);
          if (job && job.status === 'canceled') {
            newJobs.set(jobId, { ...job, status: 'running' });
            // Reverted job due to cancellation failure
          }
          return newJobs;
        });
        
        return;
      }
      
      // Fetch the updated job status
      const updatedResponse = await fetch(`${API_BASE_URL}/api/jobs/${jobId}`, {
        headers: { 'Authorization': `Bearer ${apiKey}` },
      });
      
      if (updatedResponse.ok) {
        const updatedJob: Job = await updatedResponse.json();
        setActiveJobs(prev => new Map(prev).set(jobId, updatedJob));
        
        // Trigger completion handler for canceled job
        if (onJobComplete) {
          onJobComplete(updatedJob, () => dismissJob(jobId));
        }
      }
      
      // Job cancellation completed
      
    } catch {
      // Error cancelling job
      
      // Rollback optimistic update on network error
      setActiveJobs(prev => {
        const newJobs = new Map(prev);
        const job = newJobs.get(jobId);
        if (job && job.status === 'canceled') {
          newJobs.set(jobId, { ...job, status: 'running' });
          // Reverted job due to network error
        }
        return newJobs;
      });
    }
  }, [apiKey, onJobComplete, dismissJob]);
  
  const resetDismissedJobs = useCallback(() => {
    setJobDisplayTimers(prev => {
      prev.forEach(timer => clearTimeout(timer));
      return new Map();
    });
    
    setDismissedJobs(new Set());
    setCompletedJobsNotified(new Set());
    initializedRef.current = false;
  }, []);
  
  useEffect(() => {
    return () => {
      jobDisplayTimers.forEach(timer => clearTimeout(timer));
    };
  }, [jobDisplayTimers]);

  return { addJobId, cancelJob, dismissJob, resetDismissedJobs };
};